
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Product, CartItem, Discount } from '../types';
import { supabase } from '../supabaseClient';

interface CartContextType {
  cartItems: CartItem[];
  isCartOpen: boolean;
  addToCart: (product: Product) => void;
  removeFromCart: (id: number) => void;
  updateQty: (id: number, change: number) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  cartTotal: number;
  cartCount: number;
  // Discount Related
  appliedDiscount: Discount | null;
  applyDiscountCode: (code: string) => Promise<{ success: boolean; message: string }>;
  discountedTotal: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<Discount | null>(null);

  const addToCart = (product: Product) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => item.id === product.id ? { ...item, qty: item.qty + 1 } : item);
      }
      return [...prev, { ...product, qty: 1 }];
    });
    setIsCartOpen(true);
  };

  const applyDiscountCode = async (code: string): Promise<{ success: boolean; message: string }> => {
    if (!code.trim()) return { success: false, message: "Please enter a code." };

    try {
      const { data, error } = await supabase
        .from('discounts')
        .select('*')
        .eq('code', code.toUpperCase())
        .eq('is_active', true)
        .single();

      if (error || !data) return { success: false, message: "Invalid promo code." };

      const now = new Date();
      if (data.expires_at && new Date(data.expires_at) < now) {
        return { success: false, message: "This code has expired." };
      }

      if (data.usage_limit > 0 && data.usage_count >= data.usage_limit) {
        return { success: false, message: "This code has reached its usage limit." };
      }

      setAppliedDiscount(data);
      return { success: true, message: "Code applied successfully!" };
    } catch (err) {
      return { success: false, message: "Verification failed." };
    }
  };

  const removeFromCart = (id: number) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  };

  const updateQty = (id: number, change: number) => {
    setCartItems(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = item.qty + change;
        return newQty > 0 ? { ...item, qty: newQty } : item;
      }
      return item;
    }));
  };

  const clearCart = () => {
    setCartItems([]);
    setAppliedDiscount(null);
  };

  const cartTotal = cartItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const cartCount = cartItems.reduce((acc, item) => acc + item.qty, 0);

  const discountedTotal = appliedDiscount 
    ? appliedDiscount.type === 'percentage'
      ? cartTotal * (1 - appliedDiscount.value / 100)
      : Math.max(0, cartTotal - appliedDiscount.value)
    : cartTotal;

  return (
    <CartContext.Provider value={{
      cartItems,
      isCartOpen,
      addToCart,
      removeFromCart,
      updateQty,
      clearCart,
      openCart: () => setIsCartOpen(true),
      closeCart: () => setIsCartOpen(false),
      cartTotal,
      cartCount,
      appliedDiscount,
      applyDiscountCode,
      discountedTotal
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
